import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { RendererProps } from "../BaseRenderer";
import { ChoiceModel } from "../../../models/ChoiceModel";
import { getSettingString, getSettingEnum } from "../../../models/utilities/settingUtilities";
import { QuestionModel } from "../../../models/QuestionModel";
import useStyle from "./ImageChoiceRenderer.styles";
import useQuestion from "../../../hooks/useQuestion";
import useTranslatable from "../../../hooks/useTranslatable";
import { LabelPosition } from "../../../models/enums/LabelPosition";

interface Props extends RendererProps {
    onSelect?(): void;
    selected?: boolean;
    gridRendering?: boolean;
    choice?: ChoiceModel;
    disabled?: boolean;
}

function getImageDimensions(question: QuestionModel) {
    const parentWidth = getSettingString(question.parent, "ImageWidth");
    const width = getSettingString(question, "ImageWidth", parentWidth);

    const parentHeight = getSettingString(question.parent, "ImageHeight");
    const height = getSettingString(question, "ImageHeight", parentHeight);

    return {
        width,
        height,
    };
}

function displayChoiceLabels(question: QuestionModel, gridRendering: boolean) {
    const showChoiceText = getSettingEnum(question, LabelPosition, "ShowChoiceText");
    return showChoiceText != null && showChoiceText !== LabelPosition.No && !gridRendering;
}

export default function ImageChoiceRenderer(props: Props) {
    const { choice, onSelect, gridRendering, selected, disabled } = props;

    const theme = useTheme();
    const classes = useStyle({ theme, selected });
    const divRef = React.useRef();
    const question = useQuestion();
    const choiceText = useTranslatable(choice, "TEXT");
    const imageSrc = useTranslatable(choice, "ImageUrl");

    const [active, setActive] = React.useState(false);

    const inputId = `${choice.elementId}_INPUT`;
    const labelId = `${choice.elementId}_LABEL`;
    const displayLabel = displayChoiceLabels(question, gridRendering);
    const collapsedGrid = gridRendering === false;

    return (
        <div
            className={classnames(
                classes.imageChoice,
                collapsedGrid && classes.imageChoiceCollapsed,
                disabled && classes.imageDisabled,
            )}
        >
            {imageSrc && (
                <div
                    id={inputId}
                    className={classnames(
                        classes.imageChoiceTarget,
                        disabled && classes.imageDisabled,
                    )}
                    ref={divRef}
                    role="checkbox"
                    aria-checked={selected}
                    aria-label={choiceText}
                    tabIndex={0}
                    style={{ ...getImageDimensions(question) }}
                    onMouseDown={disabled ? undefined : (event) => event.preventDefault()}
                    onFocus={disabled ? undefined : () => setActive(true)}
                    onBlur={disabled ? undefined : () => setActive(false)}
                    onMouseOver={disabled ? undefined : () => setActive(true)}
                    onMouseOut={disabled ? undefined : () => setActive(false)}
                    onClick={disabled ? undefined : onSelect}
                    onKeyDown={
                        disabled
                            ? undefined
                            : (event) => {
                                  if (event.target !== divRef.current) return;
                                  if (event.key === " ") {
                                      onSelect();
                                      event.preventDefault();
                                  }
                              }
                    }
                >
                    <div
                        className={classnames(
                            classes.imageChoiceOverlay,
                            active && classes.imageChoiceOverlayActive,
                            selected && classes.imageChoiceOverlaySelected,
                            disabled && classes.imageDisabled,
                        )}
                    />
                    <img
                        className={classnames(
                            classes.imageChoiceImage,
                            disabled && classes.imageDisabled,
                        )}
                        src={imageSrc}
                        alt=""
                        data-selected={String(selected)}
                    />
                </div>
            )}
            {displayLabel && (
                <div
                    id={labelId}
                    className={classnames(
                        classes.imageChoiceText,
                        collapsedGrid && classes.imageChoiceCollapsedText,
                        selected && classes.imageChoiceTextSelected,
                        disabled && classes.imageDisabled,
                    )}
                    onMouseOver={disabled ? undefined : () => setActive(true)}
                    onMouseOut={disabled ? undefined : () => setActive(false)}
                    onClick={
                        disabled
                            ? undefined
                            : () => {
                                  onSelect();
                                  event.preventDefault();
                              }
                    }
                    onMouseDown={(event) => event.preventDefault()}
                    dangerouslySetInnerHTML={{ __html: choiceText }}
                />
            )}
        </div>
    );
}
